import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X,
  CreditCard,
  PiggyBank,
  TrendingUp,
  Briefcase,
  Check,
  ChevronRight,
  Shield,
  DollarSign,
  Loader2,
  Wallet
} from 'lucide-react'

// Icon mapping for account categories
const getCategoryIcon = (category) => {
  switch (category?.toLowerCase()) {
    case 'savings':
      return PiggyBank
    case 'business':
      return Briefcase
    case 'investment':
      return TrendingUp
    case 'checking':
    default:
      return Wallet
  }
}

const getCategoryColor = (category) => {
  switch (category?.toLowerCase()) {
    case 'savings':
      return { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200' }
    case 'business':
      return { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200' }
    case 'investment':
      return { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200' }
    case 'checking':
    default:
      return { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' }
  }
}

export default function OpenAccountModal({ onClose, onOpenAccount, accountTypes = [], loading: externalLoading }) {
  const [step, setStep] = useState(1)
  const [selectedType, setSelectedType] = useState(null)
  const [accountName, setAccountName] = useState('')
  const [initialDeposit, setInitialDeposit] = useState('')
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  // Get the selected account type object
  const selectedAccountType = accountTypes.find(t => t.id === selectedType)

  // Validate minimum balance
  const minBalance = parseFloat(selectedAccountType?.minimum_balance || 0)
  const depositAmount = parseFloat(initialDeposit) || 0
  const meetsMinimum = depositAmount >= minBalance

  const handleSubmit = async () => {
    if (!selectedType) {
      setError('Please select an account type')
      return
    }

    if (minBalance > 0 && !meetsMinimum) {
      setError(`Minimum initial deposit of $${minBalance.toLocaleString()} required`)
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      await onOpenAccount({
        accountTypeId: selectedType,
        accountName: accountName || selectedAccountType?.name || 'New Account',
        initialDeposit: depositAmount
      })
      onClose()
    } catch (err) {
      setError(err.message || 'Failed to open account')
      setSubmitting(false)
    }
  }

  const canProceedStep1 = selectedType !== null
  const canProceedStep2 = accountName.trim() !== '' && (minBalance === 0 || meetsMinimum)
  const canSubmit = agreedToTerms && !submitting

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-2xl border border-slate-200"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b border-slate-200 bg-white sticky top-0 z-10">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Open New Account</h2>
                <p className="text-sm text-slate-500">
                  {step === 1 && 'Choose your account type'}
                  {step === 2 && 'Account details'}
                  {step === 3 && 'Review and confirm'}
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>
            </div>

            {/* Progress Steps */}
            <div className="mt-6 flex items-center gap-2">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex-1">
                  <div className={`h-2 rounded-full transition-all ${
                    s <= step ? 'bg-blue-600' : 'bg-slate-200'
                  }`} />
                </div>
              ))}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Content */}
          <div className="p-6">
            {/* Step 1: Account Type Selection */}
            {step === 1 && (
              <div className="space-y-4">
                {externalLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  </div>
                ) : accountTypes.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-slate-500">No account types available</p>
                  </div>
                ) : (
                  accountTypes.map((type, index) => {
                    const IconComponent = getCategoryIcon(type.category)
                    const colors = getCategoryColor(type.category)
                    const isSelected = selectedType === type.id
                    const features = type.features ?
                      (typeof type.features === 'string' ? JSON.parse(type.features) : type.features) : []

                    return (
                      <motion.button
                        key={type.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        onClick={() => {
                          setSelectedType(type.id)
                          setAccountName(type.name)
                          setError(null)
                        }}
                        className={`w-full p-5 rounded-xl border-2 transition-all text-left ${
                          isSelected
                            ? `${colors.border} bg-slate-50`
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-4">
                            <div className={`w-12 h-12 rounded-lg ${colors.bg} ${colors.text} flex items-center justify-center`}>
                              <IconComponent className="w-6 h-6" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="text-sm font-semibold text-slate-900">{type.name}</h3>
                              </div>
                              {type.interest_rate_apy > 0 && (
                                <p className="text-sm font-semibold text-emerald-600 mb-1">
                                  {type.interest_rate_apy}% APY
                                </p>
                              )}
                              {type.minimum_balance > 0 && (
                                <p className="text-xs text-slate-500 mb-2">
                                  Minimum balance: ${parseFloat(type.minimum_balance).toLocaleString()}
                                </p>
                              )}
                              {type.monthly_fee > 0 && (
                                <p className="text-xs text-slate-500">
                                  Monthly fee: ${parseFloat(type.monthly_fee).toFixed(2)}
                                </p>
                              )}
                              {type.description && (
                                <p className="text-xs text-slate-500 mt-2">{type.description}</p>
                              )}
                            </div>
                          </div>
                          {isSelected && (
                            <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                        {Array.isArray(features) && features.length > 0 && (
                          <ul className="mt-3 space-y-1 pl-16">
                            {features.slice(0, 4).map((feature, i) => (
                              <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                                <Check className="w-3 h-3 text-emerald-600 flex-shrink-0 mt-0.5" />
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </motion.button>
                    )
                  })
                )}
              </div>
            )}

            {/* Step 2: Account Details */}
            {step === 2 && selectedAccountType && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Account Name
                  </label>
                  <input
                    type="text"
                    value={accountName}
                    onChange={(e) => setAccountName(e.target.value)}
                    placeholder={selectedAccountType.name}
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Give your account a custom name for easy identification
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Initial Deposit
                    {minBalance > 0 && (
                      <span className="text-slate-500 font-normal ml-1">
                        (Minimum: ${minBalance.toLocaleString()})
                      </span>
                    )}
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={initialDeposit}
                      onChange={(e) => {
                        setInitialDeposit(e.target.value)
                        setError(null)
                      }}
                      placeholder="0.00"
                      className={`w-full pl-10 pr-4 py-3 bg-white border rounded-lg text-sm focus:outline-none ${
                        minBalance > 0 && initialDeposit && !meetsMinimum
                          ? 'border-red-300 focus:border-red-500'
                          : 'border-slate-200 focus:border-slate-900'
                      }`}
                    />
                  </div>
                  {minBalance > 0 && initialDeposit && !meetsMinimum && (
                    <p className="text-xs text-red-500 mt-1">
                      Minimum deposit of ${minBalance.toLocaleString()} required
                    </p>
                  )}
                </div>

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-blue-900">FDIC Insured</h4>
                      <p className="text-xs text-blue-700 mt-1">
                        Your deposits are insured up to $250,000 per depositor.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Review */}
            {step === 3 && selectedAccountType && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 mb-4">Account Summary</h3>
                  <div className="p-5 bg-slate-50 rounded-lg space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Account Type</span>
                      <span className="font-semibold text-slate-900">
                        {selectedAccountType.name}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Account Name</span>
                      <span className="font-semibold text-slate-900">
                        {accountName || selectedAccountType.name}
                      </span>
                    </div>
                    {selectedAccountType.interest_rate_apy > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Interest Rate</span>
                        <span className="font-semibold text-emerald-600">
                          {selectedAccountType.interest_rate_apy}% APY
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm pt-3 border-t border-slate-200">
                      <span className="text-slate-500">Initial Deposit</span>
                      <span className="font-semibold text-slate-900">
                        ${depositAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-white border border-slate-200 rounded-lg">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="mt-1 w-4 h-4"
                    />
                    <span className="text-xs text-slate-600 leading-relaxed">
                      I agree to the{' '}
                      <a href="#" className="text-blue-600 hover:text-blue-700 font-medium">
                        Terms and Conditions
                      </a>{' '}
                      and{' '}
                      <a href="#" className="text-blue-600 hover:text-blue-700 font-medium">
                        Privacy Policy
                      </a>
                      . I understand that this account will be subject to the account agreement.
                    </span>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
            {step > 1 ? (
              <button
                onClick={() => {
                  setStep(step - 1)
                  setError(null)
                }}
                disabled={submitting}
                className="px-6 py-2.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg transition-all text-sm font-medium disabled:opacity-50"
              >
                Back
              </button>
            ) : (
              <div />
            )}

            {step === 1 && (
              <button
                onClick={() => {
                  setStep(2)
                  setError(null)
                }}
                disabled={!canProceedStep1}
                className={`px-6 py-2.5 rounded-lg transition-all text-sm font-medium flex items-center gap-2 ${
                  !canProceedStep1
                    ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    : 'bg-slate-900 hover:bg-slate-800 text-white'
                }`}
              >
                Continue
                <ChevronRight className="w-4 h-4" />
              </button>
            )}

            {step === 2 && (
              <button
                onClick={() => {
                  if (minBalance > 0 && !meetsMinimum) {
                    setError(`Minimum initial deposit of $${minBalance.toLocaleString()} required`)
                    return
                  }
                  setStep(3)
                  setError(null)
                }}
                disabled={!canProceedStep2}
                className={`px-6 py-2.5 rounded-lg transition-all text-sm font-medium flex items-center gap-2 ${
                  !canProceedStep2
                    ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    : 'bg-slate-900 hover:bg-slate-800 text-white'
                }`}
              >
                Continue
                <ChevronRight className="w-4 h-4" />
              </button>
            )}

            {step === 3 && (
              <button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className={`px-6 py-2.5 rounded-lg transition-all text-sm font-medium flex items-center gap-2 ${
                  !canSubmit
                    ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    : 'bg-slate-900 hover:bg-slate-800 text-white'
                }`}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Opening Account...
                  </>
                ) : (
                  <>
                    Open Account
                    <Check className="w-4 h-4" />
                  </>
                )}
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
